/**
 * GARCH-Modellierung für realistische Volatilitäts-Simulation des Investitionstopfs
 *
 * Implementiert EGARCH(1,1) Modell für zeitvariable Volatilität mit:
 * - Volatility Clustering (hohe/niedrige Volatilität kommt in Clustern)
 * - Leverage Effect (negative Schocks erhöhen Volatilität stärker)
 * - Mean Reversion (Volatilität kehrt zu langfristigem Mittel zurück)
 *
 * MATHEMATISCHE KORREKTUREN (v2.4.1 - CRITICAL STABILITY IMPROVEMENTS):
 * - ROBUST MLE MIT HEURISTIC FALLBACK: Verhindert instabile Parameter-Schätzungen
 * - EXTREME VALUE PROTECTION: Automatische Begrenzung unmöglicher Renditen (>95%)
 * - VOLATILITY SPIKE PROTECTION: Begrenzt Volatilitäts-Sprünge pro Periode
 * - AUTOMATIC STATE RESET: Reset zu stabilem Zustand bei explosiver Volatilität
 * 
 * VORHERIGE VERBESSERUNGEN (v2.4.0):
 * - TRUE MAXIMUM LIKELIHOOD ESTIMATION: Vollständige MLE mit numerischer Optimierung
 * - KORREKTE RESIDUAL-BERECHNUNG: Mathematisch korrekte εₜ₋₁ = (rₜ₋₁ - μ) / σₜ₋₁
 * - UMFASSENDE DIAGNOSTIK: Ljung-Box, ARCH-LM, Sign Bias, Jarque-Bera Tests
 * - STANDARDFEHLER-SCHÄTZUNG: Hessian-basierte Parameter-Unsicherheit
 * - ERWEITERTE STATIONARITÄTSPRÜFUNG: Robuste Stabilitätsbedingungen
 * 
 * VORHERIGE KORREKTUREN (v2.3.0):
 * - Vollständig korrigierte Nelson (1991) EGARCH(1,1) Formel mit g(ε) Funktion
 * - Korrekte E[|ε|] Berechnung für Student's t-Verteilung
 * - Hinzugefügter Theta-Parameter für linearen Schock-Effekt
 * - Student's t-Verteilung für Heavy-Tail-Unterstützung
 * - Verbesserte Kalibrierung basierend auf empirischen Beziehungen
 *
 * INTEGRATED APPROACH: Nutzt bestehende historische und statistische Daten der Anwendung
 * - Erweitert 'historische-rendite' → 'historische-garch' mit Volatilitäts-Clustering
 * - Erweitert 'statistisch-rendite' → 'statistisch-garch' mit realistischer Zeitstruktur
 * - Kalibriert GARCH-Parameter automatisch aus INDEX_RETURNS (MSCI World/ACWI)
 * - Nahtlose Integration mit bestehenden RateCalculationService-Optionen
 *
 * @author Portfolio-3Topf Team
 * @version 2.4.3 - STABILITY FIX: Enforced strict β + α < 1.0 constraint across all parameter estimation methods
 * @since 2.2.0
 */

import { getIndexStatistics } from './indexConstants';
import { calculateAllStatistics } from './optimizedCalculations';

/**
 * EINHEITLICHE VARIANZ-BOUNDS (Fix #16)
 *
 * Simulation (updateGARCHState), MLE-Likelihood (calculateLogLikelihood,
 * calculateLogLikelihoodAndGradient), ModelFit und der Stabilitätstest
 * verwenden dieselbe log-Varianz-Klammer, damit das GESCHÄTZTE und das
 * SIMULIERTE Modell dieselbe Oberfläche haben. Vorher waren die Klammern
 * inkonsistent (Likelihood: Vola [10%, 100%]; ModelFit/Gradient: [1%, 200%];
 * Simulation: [1%, 100%] plus eine Dämpfung, die in keiner Likelihood vorkam).
 *
 * Ökonomisch sinnvoller Bereich: Jahres-Volatilität 1% … 150%.
 * Untere Grenze verhindert degenerierte Varianzen, obere Grenze fängt
 * numerische Ausreißer ab, lässt aber realistische Krisen-Spikes
 * (z.B. Vola 40–60% in einem Crash-Jahr) ungehindert zu.
 */
const MIN_LOG_VARIANCE = Math.log(0.0001); // σ = 1% p.a.
const MAX_LOG_VARIANCE = Math.log(2.25);   // σ = 150% p.a.

/**
 * Global cache for GARCH calibration results to avoid repeated expensive MLE computations
 * Key: indexType + mode combination
 * Value: Calibrated GARCH parameters
 */
const GLOBAL_GARCH_CALIBRATION_CACHE = new Map<string, GARCHCalibrationData>();

/**
 * Global cache for GARCH generator instances to avoid repeated object creation
 * Key: indexType + mode combination
 * Value: Reusable GARCH generator instance
 */
const GLOBAL_GARCH_GENERATOR_CACHE = new Map<string, DataIntegratedGARCHGenerator>();

/**
 * Utility functions for managing GARCH caches
 */
export const GARCHCacheUtils = {
  /**
   * Clear all GARCH calibration and generator caches
   * Use when switching between different parameter sets
   */
  clearAllCaches(): void {
    GLOBAL_GARCH_CALIBRATION_CACHE.clear();
    GLOBAL_GARCH_GENERATOR_CACHE.clear();
  },

  /**
   * Get cache statistics for monitoring
   */
  getCacheStats(): {
    calibrationCacheSize: number;
    generatorCacheSize: number;
  } {
    return {
      calibrationCacheSize: GLOBAL_GARCH_CALIBRATION_CACHE.size,
      generatorCacheSize: GLOBAL_GARCH_GENERATOR_CACHE.size,
    };
  },

  /**
   * Warm up caches with common configurations
   * Call this before starting Monte Carlo simulations for best performance
   */
  warmupCaches(): void {
    // Pre-load common configurations
    const configs = [
      { indexType: 'world' as const, mode: 'historical-garch' as const },
      { indexType: 'world' as const, mode: 'statistical-garch' as const },
      { indexType: 'acwi' as const, mode: 'historical-garch' as const },
      { indexType: 'acwi' as const, mode: 'statistical-garch' as const },
    ];

    for (const config of configs) {
      DataIntegratedGARCHGenerator.getCachedGenerator(config.indexType, config.mode);
    }
  }
};

/**
 * GARCH-Kalibrierung basierend auf bestehenden Daten der Anwendung
 *
 * Diese Klasse nutzt die bereits vorhandenen historischen Index-Daten
 * (MSCI World, ACWI) um realistische GARCH-Parameter zu schätzen.
 */
export interface GARCHCalibrationData {
  /** Historische Renditen aus bestehenden INDEX_RETURNS */
  historicalReturns: number[];

  /** Statistische Eigenschaften der historischen Daten */
  statistics: {
    mean: number;
    stdDev: number;
    skewness: number;
    kurtosis: number;
  };

  /** Geschätzte EGARCH-Parameter aus historischen Daten */
  garchParams: {
    omega: number;
    alpha: number;
    theta: number;  // Linear component of g(ε) function
    gamma: number;
    beta: number;
    // MLE estimation metadata
    standardErrors?: any;
    logLikelihood?: number;
    converged?: boolean;
    estimationMethod?: string;
    fallbackUsed?: boolean;
    iterations?: number;
  };
}

/**
 * GARCH-Zustand für sequenzielle Simulation
 */
export interface GARCHState {
  /** Aktuelle log-Varianz */
  logVariance: number;

  /** Letzte Rendite (standardisiert) */
  lastReturn: number;

  /** Letzte Volatilität */
  lastVolatility: number;

  /** Periode in der Simulation */
  period: number;
}

/**
 * Diagnostic test results for EGARCH model validation
 */
export interface DiagnosticResults {
  /** Ljung-Box test for residual autocorrelation */
  ljungBox: {
    statistic: number;
    pValue: number;
    lags: number;
    passed: boolean;
  };

  /** ARCH-LM test for remaining ARCH effects */
  archLM: {
    statistic: number;
    pValue: number;
    lags: number;
    passed: boolean;
  };

  /** Sign bias test for asymmetry validation */
  signBias: {
    negativeSignBias: number;
    positiveSignBias: number;
    jointTest: number;
    pValue: number;
    passed: boolean;
  };

  /** Jarque-Bera test for normality of standardized residuals */
  jarqueBera: {
    statistic: number;
    pValue: number;
    passed: boolean;
  };

  /** Overall model adequacy */
  overallAssessment: {
    passed: boolean;
    issues: string[];
    recommendations: string[];
  };
}

/**
 * Standardized residuals and fitted values for diagnostic testing
 */
export interface ModelFit {
  standardizedResiduals: number[];
  fittedVolatilities: number[];
  logLikelihood: number;
  aic: number;
  bic: number;
}

/**
 * Erweiterte GARCH-Generator-Klasse die bestehende Daten verwendet
 *
 * Diese Implementierung nutzt die vorhandenen historischen Index-Daten
 * der Portfolio-3Topf-Anwendung und erweitert sie um realistische
 * Volatilitäts-Modellierung.
 */
export class DataIntegratedGARCHGenerator {
  private calibrationData: GARCHCalibrationData;
  private state: GARCHState;
  private rng: () => number;
  private useHistoricalBootstrap: boolean;
  private currentInnovationDistribution: 'normal' | 'student-t';
  private studentTDegreesOfFreedom: number;

  /**
   * Deterministischer PRNG NUR für die Kalibrierung (MLE-Perturbation, Stabilitätstest).
   * Seed wird aus den Eingangsdaten abgeleitet: identische Daten ⇒ identische Parameter,
   * sessionübergreifend reproduzierbar (Ergebnis wird global gecacht).
   */
  private calibrationRng: () => number = Math.random;

  /**
   * Factory method to get or create cached GARCH generator instance
   * This avoids expensive calibration for every Monte Carlo iteration
   */
  static getCachedGenerator(
    indexType: 'world' | 'acwi' = 'world',
    mode: 'historical-garch' | 'statistical-garch' = 'historical-garch'
  ): DataIntegratedGARCHGenerator {
    const cacheKey = `${indexType}-${mode}`;
    
    // Check if we already have a generator cached
    const cachedGenerator = GLOBAL_GARCH_GENERATOR_CACHE.get(cacheKey);
    if (cachedGenerator) {
      return cachedGenerator;
    }

    // Create new generator and cache it
    const generator = new DataIntegratedGARCHGenerator(indexType, mode);
    GLOBAL_GARCH_GENERATOR_CACHE.set(cacheKey, generator);
    
    return generator;
  }

  /**
   * Resets the generator state with a new seed for a fresh iteration
   * This allows reusing the cached calibration while generating different sequences
   */
  resetForNewIteration(seed?: number): void {
    // Reset GARCH state to initial conditions
    this.state = this.initializeState();
    
    // Setup new RNG with provided seed
    // Seed-0-Fix: `seed ?` behandelte den gueltigen deterministischen Seed 0
    // (Math.abs(hash) kann 0 werden) faelschlich als "kein Seed" und fiel auf
    // Math.random zurueck -> nicht-reproduzierbar, aber unter deterministischem
    // Cache-Key gecacht. createSeededRNG(0) ist wohldefiniert (kein LCG-Fixpunkt).
    this.rng = seed !== undefined ? this.createSeededRNG(seed) : Math.random;
  }

  /**
   * Erstellt GARCH-Generator basierend auf bestehenden Index-Daten
   *
   * @param indexType - Welcher Index verwendet werden soll ('world' oder 'acwi')
   * @param mode - 'historical-garch' oder 'statistical-garch'
   * @param seed - Optional: Seed für reproduzierbare Ergebnisse
   */
  constructor(
    indexType: 'world' | 'acwi' = 'world',
    mode: 'historical-garch' | 'statistical-garch' = 'historical-garch',
    seed?: number
  ) {
    // Nutze bestehende getIndexStatistics Funktion
    const existingIndexData = getIndexStatistics(indexType);

    // Kalibriere GARCH-Parameter aus bestehenden Daten mit Caching
    this.calibrationData = this.calibrateFromExistingData(existingIndexData, indexType, mode);

    // Initialisiere Zustand
    this.state = this.initializeState();

    // Setup RNG
    // Seed-0-Fix: `seed !== undefined` statt `seed ?`, damit der gueltige
    // deterministische Seed 0 nicht faelschlich auf Math.random zurueckfaellt.
    this.rng = seed !== undefined ? this.createSeededRNG(seed) : Math.random;

    // Bestimme Modus
    this.useHistoricalBootstrap = mode === 'historical-garch';

    // Bestimme Innovation-Verteilung basierend auf Kurtosis
    //
    // KURTOSIS-KONVENTION (gilt im GESAMTEN File): calculateAllStatistics liefert
    // EXCESS-Kurtosis (m4/n/stdDev⁴ − 3). Normalverteilung ⇒ 0, nicht 3.
    // Alle Schwellen/Formeln hier verwenden daher direkt die Excess-Kurtosis.
    const excessKurtosis = this.calibrationData.statistics.kurtosis;
    if (excessKurtosis > 2) { // Excess > 2 entspricht Roh-Kurtosis > 5 (deutliche Heavy Tails)
      this.currentInnovationDistribution = 'student-t';
      // Momentenmethode für t-Verteilung: ExcessKurtosis = 6/(ν−4) ⇒ ν = 4 + 6/ExcessKurtosis
      // Ganzzahlig gerundet (für exakte χ²-Erzeugung) und geklemmt auf [3, 30]
      this.studentTDegreesOfFreedom = Math.max(3, Math.min(30, Math.round(4 + 6 / excessKurtosis)));
    } else {
      this.currentInnovationDistribution = 'normal';
      this.studentTDegreesOfFreedom = 0;
    }
  }

  /**
   * Generiert nächste jährliche Rendite mit GARCH-Volatilität
   *
   * Je nach Modus:
   * - historical-garch: Bootstraps aus historischen Daten mit GARCH-Volatilität
   * - statistical-garch: Generiert aus statistischer Verteilung mit GARCH-Volatilität
   */
  generateAnnualReturn(): number {
    // SCHRITT 1: Berechne aktuelle GARCH-Volatilität (als Dezimalzahl)
    const currentVolatility = Math.sqrt(Math.exp(this.state.logVariance));

    // SCHRITT 2: Generiere standardisierten Schock aus geeigneter Verteilung
    const standardizedShock = this.generateInnovation();

    // SCHRITT 3: Berechne Rendite basierend auf GARCH-Modell
    let annualReturn: number;

    if (this.useHistoricalBootstrap) {
      // Historical-GARCH: Bootstrap aus historischen Daten + GARCH-Volatilität
      // Basis-Rendite aus historischen Daten (in Prozent)
      const baseReturn = this.sampleFromHistoricalReturns();
      
      // Skaliere mit aktueller GARCH-Volatilität
      // Rendite = μ + σₜ × ε, wobei σₜ aus GARCH kommt
      const historicalMean = this.calibrationData.statistics.mean;
      const historicalVolatility = this.calibrationData.statistics.stdDev / 100; // Convert to decimal
      
      // Extrahiere "Schock" aus historischer Rendite
      const baseReturnDecimal = baseReturn / 100;
      const historicalMeanDecimal = historicalMean / 100;
      const normalizedShock = (baseReturnDecimal - historicalMeanDecimal) / historicalVolatility;
      
      // Verwende GARCH-Volatilität mit historischem Schock-Muster
      annualReturn = (historicalMeanDecimal + currentVolatility * normalizedShock) * 100;
    } else {
      // Statistical-GARCH: Direkte Generierung mit GARCH-Volatilität
      // rₜ = μ + σₜ × εₜ, wobei εₜ aus geeigneter Verteilung und σₜ aus GARCH
      const meanReturn = this.calibrationData.statistics.mean / 100; // Convert to decimal
      const returnDecimal = meanReturn + currentVolatility * standardizedShock;
      annualReturn = returnDecimal * 100; // Convert back to percentage
    }

    // SCHRITT 4: Sicherheitsprüfung für extreme Werte
    // Verhindere mathematisch unmögliche Renditen
    if (!isFinite(annualReturn) || Math.abs(annualReturn) > 95) {
      annualReturn = Math.max(-95, Math.min(95, annualReturn));
      
      // Bei extremen Werten: Reset GARCH state zu langfristigem Durchschnitt
      if (Math.abs(annualReturn) > 80) {
        this.resetToStableState();
        annualReturn = Math.max(-50, Math.min(50, annualReturn));
      }
    }

    // SCHRITT 5: Update GARCH-Zustand für nächste Periode
    // Der Schock εₜ wird im Update aus der AKTUELLEN Rendite und Volatilität berechnet
    this.updateGARCHState(annualReturn, currentVolatility * 100);

    return annualReturn;
  }

  /**
   * Generiert Sequenz von jährlichen Renditen für Portfolio-Simulation
   *
   * @param years - Anzahl Jahre zu simulieren
   * @returns Array von jährlichen Renditen in Prozent
   */
  generateAnnualReturns(years: number): number[] {
    const returns: number[] = [];

    for (let year = 0; year < years; year++) {
      const annualReturn = this.generateAnnualReturn();
      returns.push(annualReturn);
    }

    return returns;
  }

  /**
   * Setzt Generator zurück (für neue Monte Carlo Iteration)
   */
  reset(): void {
    this.state = this.initializeState();
  }

  /**
   * Reset zu stabilem Zustand bei extremen Volatilitäten
   * 
   * CRITICAL SAFETY METHOD v2.4.1: Verhindert explosive Volatilität
   * die zu unmöglichen Renditen führt (>100% Verlust)
   */
  private resetToStableState(): void {
    
    // Setze Volatilität auf historischen langfristigen Durchschnitt
    const longTermVariance = Math.pow(this.calibrationData.statistics.stdDev / 100, 2);
    
    this.state = {
      logVariance: Math.log(longTermVariance),
      lastReturn: this.calibrationData.statistics.mean,
      lastVolatility: Math.sqrt(longTermVariance) * 100,
      period: this.state.period
    };
  }

  /**
   * Gibt aktuelle Volatilität zurück
   */
  getCurrentVolatility(): number {
    return Math.sqrt(Math.exp(this.state.logVariance));
  }

  /**
   * Gibt Kalibrierungs-Informationen zurück (für Debugging)
   */
  getCalibrationData(): Readonly<GARCHCalibrationData> {
    return { ...this.calibrationData };
  }

  /**
   * Gibt Informationen über die verwendete Schätzungsmethode zurück
   * 
   * ESTIMATION METHOD INDICATOR v2.4.2: Für UI-Anzeige der Modellqualität
   */
  getEstimationInfo() {
    const params = this.calibrationData.garchParams;
    const isFallback = params.fallbackUsed || false;
    const method = params.estimationMethod || (isFallback ? 'Heuristic Fallback' : 'Unknown');
    
    return {
      method,
      isFallback,
      quality: isFallback ? 'STABLE' : 'OPTIMIZED',
      description: isFallback 
        ? 'Bewährte heuristische Parameter für maximale Stabilität'
        : 'Optimierte MLE-Parameter für beste statistische Anpassung',
      logLikelihood: params.logLikelihood,
      converged: params.converged,
      iterations: params.iterations || 0
    };
  }

  // ===== PRIVATE METHODS =====

  /**
   * Kalibriert GARCH-Parameter aus bestehenden INDEX_RETURNS mit globalem Caching
   */
  private calibrateFromExistingData(indexData: any, indexType: string, mode: string): GARCHCalibrationData {
    // Create cache key for this specific calibration
    const cacheKey = `${indexType}-${mode}`;
    
    // Check if we already have calibration results cached
    const cachedCalibration = GLOBAL_GARCH_CALIBRATION_CACHE.get(cacheKey);
    if (cachedCalibration) {
      return cachedCalibration;
    }

    const historicalReturns = indexData.rates; // Nutze bestehende rates aus getIndexStatistics
    const stats = calculateAllStatistics(historicalReturns); // Nutze bestehende optimierte Statistiken

    // Vereinfachte GARCH-Parameter-Schätzung basierend auf historischen Eigenschaften
    // In der Praxis würde hier Maximum Likelihood Estimation verwendet
    const garchParams = this.estimateGARCHParameters(historicalReturns, stats);

    const calibrationData: GARCHCalibrationData = {
      historicalReturns,
      statistics: {
        mean: stats.mean,
        stdDev: stats.stdDev,
        skewness: stats.skewness,
        kurtosis: stats.kurtosis,
      },
      garchParams,
    };

    // Cache the calibration results for future use
    GLOBAL_GARCH_CALIBRATION_CACHE.set(cacheKey, calibrationData);
    
    return calibrationData;
  }

  /**
   * True Maximum Likelihood Estimation for EGARCH parameters
   * 
   * Implements proper MLE with numerical optimization according to Nelson (1991)
   * and Bollerslev (1986) specifications for GARCH models.
   * 
   * @param returns Historical returns data
   * @param stats Basic statistics for initialization
   * @returns Optimized EGARCH parameters with standard errors
   */
  private estimateGARCHParameters(returns: number[], stats: any) {
    // Validiere Eingabedaten
    // Note: Less than 50 data points may reduce estimation robustness

    // SCHRITT 0: REPRODUZIERBARE KALIBRIERUNG
    // Deterministischer, geseedeter PRNG statt Math.random() — Seed aus den
    // Renditedaten abgeleitet, damit identische Daten identische Parameter liefern.
    this.calibrationRng = this.createSeededRNG(this.deriveCalibrationSeed(returns));

    // SCHRITT 1: Initialisiere Parameter mit heuristischen Startwerten
    const initialParams = this.getInitialParameterGuess(returns, stats);
    
    // SCHRITT 2: Maximum Likelihood Estimation mit numerischer Optimierung
    const mleResult = this.maximizeLikelihood(returns, initialParams);
    
    // SCHRITT 3: Validiere und adjustiere falls nötig
    const validatedParams = this.validateAndAdjustMLEParameters(mleResult.parameters);
    
    // SCHRITT 4: Berechne Standardfehler und Konfidenzintervalle
    const standardErrors = this.calculateStandardErrors(returns, validatedParams);
    
    // EGARCH estimation completed

    // EHRLICHE METHODEN-KENNZEICHNUNG: Der Heuristik-Fallback (fallbackUsed) wird
    // als solcher ausgewiesen und NICHT als 'MLE (Maximum Likelihood)' gemeldet.
    const fallbackUsed = (mleResult as any).fallbackUsed === true;
    const estimationMethod = fallbackUsed
      ? 'Heuristik (Fallback)'
      : (mleResult.converged ? 'MLE (Maximum Likelihood)' : 'MLE (nicht konvergiert)');

    return {
      ...validatedParams,
      standardErrors,
      logLikelihood: mleResult.logLikelihood,
      converged: mleResult.converged,
      estimationMethod,
      fallbackUsed,
      iterations: mleResult.iterations || 0
    };
  }

  /**
   * Berechnet heuristische Startwerte für MLE Optimierung
   */
  private getInitialParameterGuess(_returns: number[], stats: any) {
    // IMPROVED INITIAL GUESSES v2.4.2: Datenbasierte Startwerte für bessere MLE Konvergenz
    const historicalVol = stats.stdDev;
    // stats.kurtosis ist bereits EXCESS-Kurtosis (siehe Konvention im Konstruktor)
    const excessKurtosis = Math.max(0, stats.kurtosis);
    
    // STABILITY FIX: Conservative starting values that guarantee β + α < 1.0
    let beta = Math.min(0.88, 0.82 + 0.04 * Math.min(1, historicalVol / 20)); // Reduced from 0.95
    // α-Floor 0.05 (konsistent mit enforceParameterConstraints und Heuristik)
    let alpha = Math.max(0.05, Math.min(0.15, 0.06 + 0.04 * Math.min(1, excessKurtosis / 5))); // Reduced from 0.25

    // Ensure starting values are stable
    const persistenceSum = beta + alpha;
    if (persistenceSum >= 0.95) {
      const targetSum = 0.90;
      const scale = targetSum / persistenceSum;
      beta *= scale;
      alpha *= scale;

      // Maintain minimum bounds
      alpha = Math.max(0.05, alpha);
      beta = Math.max(0.75, beta);
    }
    
    // γ-VORZEICHENFIX: In Nelsons g(ε) = θ·ε + γ·(|ε| − E|ε|) ist γ der
    // MAGNITUDEN-Koeffizient und muss POSITIV sein, damit ein großer Schock
    // (egal welches Vorzeichen) die Volatilität ERHÖHT (Volatilitäts-Clustering).
    // Vorher wurde γ hier negativ erzwungen → Clustering lief invertiert
    // (Crash SENKTE die Folgejahres-Vola). θ ist der SIGNIERTE Koeffizient
    // (Leverage-Effekt): für Aktien θ < 0. Empirische EGARCH-Schätzungen für
    // Aktienindizes: γ ≈ 0.1–0.25, θ ≈ −0.05 bis −0.15.
    const theta = Math.max(-0.15, Math.min(0.0, -0.05 + 0.03 * Math.max(-1, Math.min(1, stats.skewness))));
    const gamma = Math.max(0.05, Math.min(0.3, 0.12 - 0.04 * Math.max(-1, Math.min(0, stats.skewness))));

    // Omega für realistische langfristige Volatilität
    // ω = (1−β)·log(σ²) ohne Zusatzfaktor: targetLogVariance ist NEGATIV,
    // ein Faktor < 1 würde ω weniger negativ machen und die unbedingte
    // Volatilität deutlich über den historischen Wert anheben (~25% statt ~17.7%).
    const targetLogVariance = Math.log(Math.pow(stats.stdDev / 100, 2));
    const omega = (1 - beta) * targetLogVariance;
    
    return { omega, alpha, theta, gamma, beta };
  }

  /**
   * Maximum Likelihood Estimation mit robuster Optimierung
   * 
   * CRITICAL FIX v2.4.1: Fallback zu bewährten heuristischen Parametern
   * wenn MLE instabil wird oder extreme Werte erzeugt.
   * 
   * Maximiert die Log-Likelihood Funktion:
   * L = -0.5 * Σ(ln(2π) + ln(σₜ²) + εₜ²/σₜ²)
   */
  private maximizeLikelihood(returns: number[], initialParams: any) {
    // SCHRITT 1: Versuche echte MLE Optimierung
    const mleResult = this.attemptTrueMLE(returns, initialParams);
    
    // SCHRITT 2: Validiere MLE Ergebnisse auf Stabilität
    if (this.isMLEResultStable(mleResult, returns)) {
      // EGARCH MLE: Successful convergence with stable parameters
      return mleResult;
    }
    
    // SCHRITT 3: Fallback zu bewährten heuristischen Parametern
    // EGARCH MLE: Unstable results, falling back to robust heuristic estimation
    // EHRLICHE BERICHTERSTATTUNG: converged=false, da die MLE NICHT konvergiert ist —
    // die Parameter stammen aus der Heuristik, nicht aus der Optimierung.
    const heuristicParams = this.getHeuristicParameters(returns);
    const heuristicLikelihood = this.calculateLogLikelihood(returns, heuristicParams);

    return {
      parameters: heuristicParams,
      logLikelihood: heuristicLikelihood,
      iterations: 0,
      converged: false,
      fallbackUsed: true
    };
  }

  /**
   * Verbesserte MLE Optimierung mit Multi-Start Ansatz
   * 
   * ENHANCED CONVERGENCE v2.4.2: Mehrere Startpunkte und robustere Optimierung
   */
  private attemptTrueMLE(returns: number[], initialParams: any) {
    const maxIterations = 100; // Erhöht für bessere Konvergenz
    const tolerance = 1e-4; // Ausgewogene Toleranz
    
    // MULTI-START APPROACH: Versuche mehrere Startpunkte
    const startingPoints = [
      { ...initialParams }, // Original
      { ...initialParams, alpha: initialParams.alpha * 0.7, beta: initialParams.beta * 0.95 }, // Konservativer
      { ...initialParams, alpha: initialParams.alpha * 1.3, gamma: initialParams.gamma * 1.2 }, // Aggressiver
    ];
    
    let bestResult = {
      parameters: initialParams,
      logLikelihood: -Infinity,
      iterations: 0,
      converged: false
    };
    
    // Versuche jeden Startpunkt
    for (const startParams of startingPoints) {
      const result = this.optimizeFromStartingPoint(returns, startParams, maxIterations, tolerance);
      
      if (result.logLikelihood > bestResult.logLikelihood) {
        bestResult = result;
      }
    }
    
    return bestResult;
  }

  /**
   * Optimiert Parameter von einem spezifischen Startpunkt
   */
  private optimizeFromStartingPoint(returns: number[], initialParams: any, maxIterations: number, tolerance: number) {
    let currentParams = { ...initialParams };
    let bestLogLikelihood = -Infinity;
    let bestParams = { ...initialParams };
    let iteration = 0;
    let converged = false;
    let noImprovementCount = 0;

    // Berechne initiale Likelihood
    const initialLikelihood = this.calculateLogLikelihood(returns, currentParams);
    bestLogLikelihood = initialLikelihood;

    for (let iter = 0; iter < maxIterations; iter++) {
      iteration = iter;
      
      // Berechne aktuelle Likelihood
      const currentLikelihood = this.calculateLogLikelihood(returns, currentParams);
      
      // Prüfe auf Verbesserung
      if (currentLikelihood > bestLogLikelihood + tolerance) {
        bestLogLikelihood = currentLikelihood;
        bestParams = { ...currentParams };
        noImprovementCount = 0;
      } else {
        noImprovementCount++;
      }
      
      // Konvergenz: Keine Verbesserung in letzten 15 Iterationen
      if (noImprovementCount >= 15) {
        converged = true;
        break;
      }
      
      // ADAPTIVE PARAMETER PERTURBATION
      const stepSize = this.getAdaptiveStepSize(iter, noImprovementCount);
      const perturbedParams = this.perturbParametersAdaptive(currentParams, stepSize);
      const perturbedLikelihood = this.calculateLogLikelihood(returns, perturbedParams);
      
      if (perturbedLikelihood > currentLikelihood) {
        currentParams = perturbedParams;
      }
      
      // Parameter-Constraints durchsetzen
      currentParams = this.enforceParameterConstraints(currentParams);
    }

    return {
      parameters: bestParams,
      logLikelihood: bestLogLikelihood,
      iterations: iteration,
      converged
    };
  }

  /**
   * Adaptive Schrittweite basierend auf Optimierungsfortschritt
   */
  private getAdaptiveStepSize(iteration: number, noImprovementCount: number): number {
    // Größere Schritte am Anfang, kleinere bei Konvergenz
    const baseStep = 0.05;
    const iterationFactor = Math.max(0.1, 1 - iteration / 100);
    const improvementFactor = Math.max(0.5, 1 - noImprovementCount / 10);
    
    return baseStep * iterationFactor * improvementFactor;
  }

  /**
   * Adaptive Parameter-Perturbation
   */
  private perturbParametersAdaptive(params: any, stepSize: number) {
    // Verschiedene Perturbations-Strategien für verschiedene Parameter
    // Deterministischer calibrationRng statt Math.random() für reproduzierbare Kalibrierung
    return {
      omega: params.omega + (this.calibrationRng() - 0.5) * stepSize * 0.5,
      alpha: params.alpha + (this.calibrationRng() - 0.5) * stepSize * 0.3,
      theta: params.theta + (this.calibrationRng() - 0.5) * stepSize * 0.2,
      gamma: params.gamma + (this.calibrationRng() - 0.5) * stepSize * 0.3,
      beta: params.beta + (this.calibrationRng() - 0.5) * stepSize * 0.1 // Kleinere Schritte für β
    };
  }

  /**
   * Prüft ob MLE Ergebnisse stabil und realistisch sind
   * 
   * STABILITY FIX v2.4.3: Explizite Stationaritätsprüfung β + α < 1.0
   */
  private isMLEResultStable(mleResult: any, returns: number[]): boolean {
    const params = mleResult.parameters;
    
    // Test 1: CRITICAL - Stationarity condition β + α < 1.0
    const persistenceSum = params.beta + params.alpha;
    if (persistenceSum >= 1.0) {return false;}
    
    // Test 2: Grundlegende Parameter-Validität
    if (params.alpha <= 0 || params.alpha > 0.5) {return false;} // Reduced from 0.8 for stability
    if (params.beta <= 0 || params.beta >= 0.95) {return false;} // Reduced from 0.999 for stability
    // γ-VORZEICHENFIX: γ muss POSITIV sein (Magnituden-Term ⇒ Clustering)
    if (params.gamma <= 0 || params.gamma > 0.5) {return false;}
    if (params.theta < -0.3 || params.theta > 0.1) {return false;}
    
    // Test 3: Likelihood-Plausibilität
    if (!isFinite(mleResult.logLikelihood)) {return false;}
    if (mleResult.logLikelihood < -2000 * returns.length) {return false;} // Weniger strikt
    
    // Test 3: Volatilitätsgenerierung (realistischere Grenzen)
    try {
      const testGenerator = this.createTestGenerator(params);
      const testReturns = testGenerator.generateAnnualReturns(20); // Längerer Test
      
      // Prüfe auf extreme Werte (realistischere Grenzen)
      const hasExtremeReturns = testReturns.some((r: number) => Math.abs(r) > 200); // Erhöht von 100
      if (hasExtremeReturns) {return false;}
      
      // Prüfe auf NaN/Infinity
      const hasInvalidReturns = testReturns.some((r: number) => !isFinite(r));
      if (hasInvalidReturns) {return false;}
      
      // Test 4: Prüfe realistische Volatilitätsbereiche
      const volatilities = testReturns.map(() => {
        // Use the mock generator's volatility directly
        return testGenerator.getCurrentVolatility() * 100;
      });
      
      const avgVolatility = volatilities.reduce((sum: number, v: number) => sum + v, 0) / volatilities.length;
      if (avgVolatility < 1 || avgVolatility > 150) {return false;} // Realistische Volatilitätsgrenzen
      
    } catch (error) {
      // MLE stability test failed
      return false;
    }
    
    return true;
  }

  /**
   * Erstellt Test-Generator für Stabilitätsprüfung
   * 
   * SIMPLIFIED v2.4.2: Vereinfachte Stabilitätsprüfung ohne Generator-Erstellung
   */
  private createTestGenerator(params: any) {
    // Vereinfachte Stabilitätsprüfung: Prüfe nur Parameter-Plausibilität
    // Anstatt einen vollständigen Generator zu erstellen
    
    try {
      // Teste einfache Volatilitätsberechnung
      const testLogVariance = Math.log(0.04); // 20% Volatilität
      
      // Teste GARCH-Update mit neutralen Werten
      const { omega, alpha, theta, gamma, beta } = params;
      const testEpsilon = 0.5;
      const expectedAbsEpsilon = Math.sqrt(2 / Math.PI);
      const gFunction = theta * testEpsilon + gamma * (Math.abs(testEpsilon) - expectedAbsEpsilon);
      const newLogVariance = omega + beta * testLogVariance + alpha * gFunction;
      
      // Prüfe auf realistische Ergebnisse — einheitliche Bounds (Fix #16)
      if (!isFinite(newLogVariance) || newLogVariance < MIN_LOG_VARIANCE || newLogVariance > MAX_LOG_VARIANCE) {
        throw new Error('Unrealistic volatility dynamics');
      }
      
      // Erstelle Mock-Generator für API-Kompatibilität
      // Deterministischer calibrationRng statt Math.random(), damit der
      // Stabilitätstest (und damit die Kalibrierung) reproduzierbar ist
      return {
        generateAnnualReturns: (n: number) => {
          const mockReturns = [];
          for (let i = 0; i < n; i++) {
            // Einfache Mock-Renditen basierend auf Parametern
            const mockReturn = 10 + (this.calibrationRng() - 0.5) * Math.sqrt(Math.exp(newLogVariance)) * 100;
            mockReturns.push(Math.max(-95, Math.min(95, mockReturn)));
          }
          return mockReturns;
        },
        getCurrentVolatility: () => Math.sqrt(Math.exp(newLogVariance))
      };
      
    } catch (error) {
      throw new Error(`Test generator creation failed: ${error}`);
    }
  }

  /**
   * Robuste heuristische Parameter-Schätzung (Fallback)
   */
  private getHeuristicParameters(returns: number[]) {
    const stats = calculateAllStatistics(returns);
    
    // Bewährte empirische Parameter für Stabilität
    const historicalVol = stats.stdDev;
    
    // STABILITY FIX: Ensure β + α < 1.0 by design
    let beta = Math.min(0.88, 0.82 + 0.04 * Math.min(1, historicalVol / 20)); // Reduced max from 0.92 to 0.88
    // stats.kurtosis ist bereits EXCESS-Kurtosis (siehe Konvention im Konstruktor)
    let alpha = Math.max(0.05, Math.min(0.12, 0.08 + 0.03 * Math.max(0, stats.kurtosis) / 5)); // Reduced max from 0.15 to 0.12
    
    // Guarantee stability constraint: β + α < 1.0
    const persistenceSum = beta + alpha;
    if (persistenceSum >= 0.98) { // Conservative threshold
      const targetSum = 0.95;
      const scale = targetSum / persistenceSum;
      beta *= scale;
      alpha *= scale;
      
      // Ensure minimum viable values
      alpha = Math.max(0.05, alpha);
      beta = Math.max(0.75, beta);
    }
    
    // γ-VORZEICHENFIX (siehe getInitialParameterGuess): γ > 0 = Magnitude/Clustering,
    // θ < 0 = Leverage. Mehr negative Schiefe ⇒ etwas stärkeres Clustering / Leverage.
    const theta = Math.max(-0.15, Math.min(0.0, -0.04 + 0.02 * Math.max(-1, Math.min(1, stats.skewness))));
    const gamma = Math.max(0.05, Math.min(0.15, 0.10 - 0.02 * Math.max(-1, Math.min(0, stats.skewness))));

    // Omega für stabile langfristige Volatilität
    const targetLogVariance = Math.log(Math.pow(stats.stdDev / 100, 2));
    const omega = (1 - beta) * targetLogVariance;
    
    return { omega, alpha, theta, gamma, beta };
  }


  /**
   * Vereinfachte Log-Likelihood Berechnung
   */
  private calculateLogLikelihood(returns: number[], params: any): number {
    const { omega, alpha, theta, gamma, beta } = params;
    const n = returns.length;
    const mean = returns.reduce((sum, r) => sum + r, 0) / n;
    
    let logLikelihood = 0;
    const stats = calculateAllStatistics(returns);
    let logVariance = Math.log(Math.pow(stats.stdDev / 100, 2));

    for (let t = 1; t < Math.min(n, 100); t++) { // Begrenzt für Performance
      const currentReturn = returns[t] / 100;
      const meanDecimal = mean / 100;
      const currentVolatility = Math.sqrt(Math.exp(logVariance));

      // Verhindere Division durch Null
      if (currentVolatility < 1e-8) {
        return -Infinity;
      }

      const epsilon = (currentReturn - meanDecimal) / currentVolatility;

      // Likelihood Beitrag
      logLikelihood += -0.5 * (Math.log(2 * Math.PI) + logVariance + epsilon * epsilon);

      // LAG-FIX: Update für σ²ₜ₊₁ verwendet den AKTUELLEN Schock εₜ
      // log(σₜ₊₁²) = ω + β·log(σₜ²) + α·g(εₜ) — konsistent mit updateGARCHState
      const expectedAbsEpsilon = Math.sqrt(2 / Math.PI);
      const gFunction = theta * epsilon + gamma * (Math.abs(epsilon) - expectedAbsEpsilon);
      const newLogVariance = omega + beta * logVariance + alpha * gFunction;

      // Stabilität sicherstellen — einheitliche Bounds (Fix #16), identisch zur Simulation
      logVariance = Math.max(MIN_LOG_VARIANCE, Math.min(MAX_LOG_VARIANCE, newLogVariance));

      // Abbrechen bei instabilen Werten
      if (!isFinite(logLikelihood) || !isFinite(logVariance)) {
        return -Infinity;
      }
    }
    
    return logLikelihood;
  }

  /**
   * Berechnet Log-Likelihood und Gradienten für EGARCH
   * 
   * Log-Likelihood: L = -0.5 * Σ(ln(2π) + ln(σₜ²) + εₜ²/σₜ²)
   */
  private calculateLogLikelihoodAndGradient(returns: number[], params: any) {
    const { omega, alpha, theta, gamma, beta } = params;
    const n = returns.length;
    const mean = returns.reduce((sum, r) => sum + r, 0) / n;
    
    let logLikelihood = 0;
    const gradient = { omega: 0, alpha: 0, theta: 0, gamma: 0, beta: 0 };
    
    // Berechne historische Volatilität aus returns direkt
    const stats = calculateAllStatistics(returns);
    const initialVolatility = stats.stdDev / 100; // Convert to decimal
    let logVariance = Math.log(Math.pow(initialVolatility, 2));

    for (let t = 1; t < n; t++) {
      // Berechne standardisierte Residuen
      const currentReturn = returns[t] / 100; // Convert to decimal
      const meanDecimal = mean / 100;
      const currentVolatility = Math.sqrt(Math.exp(logVariance));
      const epsilon = (currentReturn - meanDecimal) / currentVolatility;

      // LAG-FIX: g(ε) mit AKTUELLEM Schock εₜ für das Update von σ²ₜ₊₁
      const expectedAbsEpsilon = Math.sqrt(2 / Math.PI);
      const gFunction = theta * epsilon + gamma * (Math.abs(epsilon) - expectedAbsEpsilon);

      // Update log-variance mit EGARCH
      const newLogVariance = omega + beta * logVariance + alpha * gFunction;
      // Einheitliche Bounds (Fix #16), identisch zu Likelihood und Simulation
      const boundedLogVariance = Math.max(MIN_LOG_VARIANCE, Math.min(MAX_LOG_VARIANCE, newLogVariance));

      // Log-Likelihood Beitrag (Varianz der Periode t, VOR dem Update)
      logLikelihood += -0.5 * (Math.log(2 * Math.PI) + logVariance + epsilon * epsilon);
      
      // Gradient Berechnung (numerische Approximation für Einfachheit)
      const delta = 1e-8;
      gradient.omega += this.numericalGradient(returns, params, 'omega', t, delta);
      gradient.alpha += this.numericalGradient(returns, params, 'alpha', t, delta);
      gradient.theta += this.numericalGradient(returns, params, 'theta', t, delta);
      gradient.gamma += this.numericalGradient(returns, params, 'gamma', t, delta);
      gradient.beta += this.numericalGradient(returns, params, 'beta', t, delta);
      
      // Update für nächste Iteration
      logVariance = boundedLogVariance;
    }
    
    return { logLikelihood, gradient };
  }

  /**
   * Numerische Gradient-Approximation für MLE
   */
  private numericalGradient(returns: number[], params: any, paramName: string, t: number, delta: number): number {
    // Finite difference approximation
    const paramsPlus = { ...params };
    const paramsMinus = { ...params };
    
    paramsPlus[paramName] += delta;
    paramsMinus[paramName] -= delta;
    
    const likelihoodPlus = this.calculateSingleStepLogLikelihood(returns, paramsPlus, t);
    const likelihoodMinus = this.calculateSingleStepLogLikelihood(returns, paramsMinus, t);
    
    return (likelihoodPlus - likelihoodMinus) / (2 * delta);
  }

  /**
   * Berechnet Log-Likelihood für einen einzelnen Zeitschritt
   */
  private calculateSingleStepLogLikelihood(returns: number[], _params: any, t: number): number {
    // Vereinfachte Einzelschritt-Likelihood für Gradient
    const mean = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    
    // Approximiere mit verfügbaren Daten
    const currentReturn = returns[t] / 100;
    const meanDecimal = mean / 100;
    const stats = calculateAllStatistics(returns);
    const estimatedVariance = Math.pow(stats.stdDev / 100, 2);
    const epsilon = (currentReturn - meanDecimal) / Math.sqrt(estimatedVariance);
    
    return -0.5 * (Math.log(2 * Math.PI) + Math.log(estimatedVariance) + epsilon * epsilon);
  }


  /**
   * Setzt Parameter-Constraints durch
   */
  private enforceParameterConstraints(params: any) {
    return {
      omega: params.omega, // Omega kann negativ sein
      // α-Floor 0.05: empirische Untergrenze für den ARCH-Effekt bei Aktienindizes,
      // konsistent mit der App-eigenen Validierung (GARCHValidationPanel erwartet
      // α ∈ [0.05, 0.30]) und dem Heuristik-Fallback (α ≥ 0.05). Die Likelihood ist
      // bei n≈55 Jahresrenditen nahezu flach in α (LR-Spread < 1 nat) — ohne Floor
      // driftet der Optimierer zu α ≈ 0 und deaktiviert das Kern-Feature Clustering.
      alpha: Math.max(0.05, Math.min(0.5, params.alpha)),
      theta: Math.max(-0.3, Math.min(0.05, params.theta)),  // θ: Leverage-Koeffizient (signierter Term), für Aktien < 0
      gamma: Math.max(0.05, Math.min(0.4, params.gamma)),   // γ > 0: Magnituden-Koeffizient (Volatilitäts-Clustering)
      beta: Math.max(0.1, Math.min(0.999, params.beta))     // β < 1 für Stationarität
    };
  }

  /**
   * Validiert und adjustiert MLE Parameter
   */
  private validateAndAdjustMLEParameters(params: any) {
    // Prüfe erweiterte Stationaritätsbedingungen
    if (!this.validateEGARCHStationarity(params)) {
      // EGARCH MLE: Parameter violate stationarity, adjusting...
      return this.adjustForStationarity(params);
    }
    
    return params;
  }

  /**
   * Erweiterte EGARCH Stationaritätsprüfung
   */
  private validateEGARCHStationarity(params: any): boolean {
    const { alpha, theta, gamma, beta } = params;
    
    // Grundbedingung: β < 1
    if (beta >= 1) {return false;}
    
    // α > 0 für ARCH-Effekt
    if (alpha <= 0) {return false;}
    
    // Praktische Stabilitätsgrenzen
    // γ-VORZEICHENFIX: γ muss POSITIV sein (Magnituden-Term ⇒ Clustering)
    if (gamma <= 0 || gamma > 0.5) {return false;}
    if (theta < -0.3 || theta > 0.1) {return false;}

    // Zusätzliche Bedingung: Existenz unbedingter Momente
    // Für EGARCH komplexer, vereinfachte Prüfung
    const persistenceSum = Math.abs(beta) + Math.abs(alpha) * 0.5; // Approximation
    if (persistenceSum >= 0.999) {return false;}
    
    return true;
  }

  /**
   * Adjustiert Parameter für Stationarität
   */
  private adjustForStationarity(params: any) {
    const adjusted = { ...params };
    
    // Reduziere β falls zu hoch
    if (adjusted.beta >= 1) {
      adjusted.beta = 0.95;
    }
    
    // Begrenze andere Parameter
    // γ-VORZEICHENFIX: γ auf POSITIVEN Bereich klemmen (Magnitude/Clustering),
    // θ darf für den Leverage-Effekt deutlich negativ sein.
    adjusted.alpha = Math.max(0.05, Math.min(0.3, adjusted.alpha));
    adjusted.theta = Math.max(-0.3, Math.min(0.05, adjusted.theta));
    adjusted.gamma = Math.max(0.05, Math.min(0.4, adjusted.gamma));
    
    // CRITICAL FIX: Enforce strict stationarity condition β + α < 1.0
    const persistenceSum = adjusted.beta + adjusted.alpha;
    if (persistenceSum >= 1.0) {
      // Scale down proportionally to ensure stability while preserving relative importance
      const targetSum = 0.98; // Leave small safety margin
      const scale = targetSum / persistenceSum;
      
      adjusted.beta *= scale;
      adjusted.alpha *= scale;
      
      // Ensure minimum bounds are still respected after scaling
      adjusted.alpha = Math.max(0.01, adjusted.alpha);
      adjusted.beta = Math.max(0.7, adjusted.beta);
      
      // Re-check and final adjustment if still unstable
      const finalSum = adjusted.beta + adjusted.alpha;
      if (finalSum >= 1.0) {
        // Force conservative stable values as last resort
        adjusted.beta = 0.85;
        adjusted.alpha = 0.12;
      }
    }
    
    return adjusted;
  }

  /**
   * Berechnet Standardfehler der Parameter (Hessian-basiert)
   */
  private calculateStandardErrors(returns: number[], params: any) {
    // Vereinfachte Standardfehler-Schätzung über numerische Hessian
    const delta = 1e-6;
    const paramNames = ['omega', 'alpha', 'theta', 'gamma', 'beta'];
    const standardErrors: any = {};
    
    for (const paramName of paramNames) {
      // Zweite Ableitung approximieren
      const paramsPlus = { ...params };
      const paramsMinus = { ...params };
      const paramsCenter = { ...params };
      
      paramsPlus[paramName] += delta;
      paramsMinus[paramName] -= delta;
      
      const likelihoodPlus = this.calculateLogLikelihoodAndGradient(returns, paramsPlus).logLikelihood;
      const likelihoodMinus = this.calculateLogLikelihoodAndGradient(returns, paramsMinus).logLikelihood;
      const likelihoodCenter = this.calculateLogLikelihoodAndGradient(returns, paramsCenter).logLikelihood;
      
      // Zweite Ableitung (Hessian Diagonal)
      const secondDerivative = (likelihoodPlus - 2 * likelihoodCenter + likelihoodMinus) / (delta * delta);
      
      // Standardfehler = sqrt(-1/H_ii) wobei H_ii = zweite Ableitung
      standardErrors[paramName] = secondDerivative < 0 ? Math.sqrt(-1 / secondDerivative) : NaN;
    }
    
    return standardErrors;
  }


  /**
   * Initialisiert GARCH-Zustand mit langfristigen Werten
   * 
   * Verwendet die unbedingte Varianz als Startwert für log(σ₀²)
   * und setzt realistische Anfangswerte für Rendite und Volatilität
   */
  private initializeState(): GARCHState {
    // Unbedingte Varianz in Dezimalform (z.B. 0.0004 für 20% Volatilität)
    const longTermVariance = Math.pow(this.calibrationData.statistics.stdDev / 100, 2);
    const longTermVolatility = Math.sqrt(longTermVariance);

    return {
      logVariance: Math.log(longTermVariance),
      lastReturn: this.calibrationData.statistics.mean, // Anfangswert: historischer Mittelwert (in %)
      lastVolatility: longTermVolatility * 100, // Volatilität in % für Konsistenz
      period: 0,
    };
  }

  /**
   * Samples eine zufällige historische Rendite (für historical-garch Modus)
   */
  private sampleFromHistoricalReturns(): number {
    const returns = this.calibrationData.historicalReturns;
    const randomIndex = Math.floor(this.rng() * returns.length);
    return returns[randomIndex];
  }

  /**
   * Aktualisiert GARCH-Zustand basierend auf aktueller Rendite
   *
   * Implementiert die mathematisch korrekte Nelson (1991) EGARCH(1,1) Formel:
   * log(σₜ₊₁²) = ω + β·log(σₜ²) + α·g(εₜ)
   *
   * wobei g(ε) = θ·ε + γ·(|ε| - E[|ε|])
   *
   * KRITISCHE KORREKTUR (Lag-Fix): Der Schock εₜ wird aus der AKTUELLEN Rendite
   * und der AKTUELLEN Volatilität berechnet: εₜ = (rₜ - μ) / σₜ.
   * Vorher wurde fälschlich εₜ₋₁ (aus lastReturn/lastVolatility) verwendet,
   * wodurch Volatilitäts-Cluster ein Jahr zu spät entstanden.
   */
  private updateGARCHState(currentReturn: number, currentVolatility: number): void {
    const { omega, alpha, theta, gamma, beta } = this.calibrationData.garchParams;

    // SCHRITT 1: Berechne εₜ aus AKTUELLER Rendite und AKTUELLER Volatilität
    // εₜ = (rₜ - μ) / σₜ
    // Hinweis: Berechnung aus der (ggf. begrenzten) Rendite statt aus dem rohen
    // Innovations-Schock, damit Clamping und Bootstrap-Modus korrekt einfließen.
    const meanReturn = this.calibrationData.statistics.mean / 100; // Convert to decimal
    const currentReturnDecimal = currentReturn / 100; // Convert to decimal
    const currentVolatilityDecimal = currentVolatility / 100; // Convert to decimal

    // Mathematisch korrekte Standardisierung
    const standardizedResidual = (currentReturnDecimal - meanReturn) / currentVolatilityDecimal;

    // SCHRITT 2: Berechnung der korrekten E[|ε|] basierend auf verwendeter Verteilung
    let expectedAbsResidual: number;

    if (this.currentInnovationDistribution === 'student-t') {
      // E[|ε|] für varianz-standardisierte Student's t-Innovationen mit ν Freiheitsgraden:
      // E[|t_ν|] = 2·√ν·Γ((ν+1)/2) / (√π·(ν−1)·Γ(ν/2)) für ν > 1
      // Unsere Innovationen sind mit √((ν−2)/ν) auf Var(ε)=1 standardisiert, daher:
      // E[|ε|] = 2·√(ν−2)·Γ((ν+1)/2) / (√π·(ν−1)·Γ(ν/2))
      // Damit gilt E[g(ε)] ≈ 0 und es entsteht kein Langfrist-Varianz-Bias.
      const nu = this.studentTDegreesOfFreedom;
      if (nu > 2) {
        expectedAbsResidual = (2 * Math.sqrt(nu - 2) * this.gammaFunction((nu + 1) / 2)) /
          (Math.sqrt(Math.PI) * (nu - 1) * this.gammaFunction(nu / 2));
      } else {
        expectedAbsResidual = Math.sqrt(2 / Math.PI); // Fallback auf Normal
      }
    } else {
      // E[|ε|] = √(2/π) ≈ 0.7979 für standardnormalverteilte Residuen
      expectedAbsResidual = Math.sqrt(2 / Math.PI);
    }

    // SCHRITT 3: Vollständige g(ε) Funktion nach Nelson (1991)
    // g(ε) = θ·ε + γ·(|ε| - E[|ε|])
    const linearComponent = theta * standardizedResidual;
    const asymmetricComponent = gamma * (Math.abs(standardizedResidual) - expectedAbsResidual);
    const gFunction = linearComponent + asymmetricComponent;

    // SCHRITT 4: EGARCH(1,1) Update nach Nelson (1991)
    // log(σₜ₊₁²) = ω + β·log(σₜ²) + α·g(εₜ)
    const persistenceEffect = beta * this.state.logVariance;
    const innovationEffect = alpha * gFunction;
    const newLogVariance = omega + persistenceEffect + innovationEffect;

    // SCHRITT 5: Stabilitätsbegrenzung — einheitliche Bounds (Fix #16)
    // Verhindere degenerierte oder explosive Varianzen. Dieselbe Klammer wie in
    // der MLE-Likelihood und im ModelFit, damit das simulierte Modell exakt dem
    // geschätzten entspricht (Jahres-Vola 1% … 150%).
    //
    // DÄMPFUNG ENTFERNT (Fix #16): Die frühere ±0.5-Begrenzung der
    // log-Varianz-Änderung pro Periode (Kommentar behauptete fälschlich
    // "50% Änderung"; tatsächlich Faktor e^±0.5 ≈ ×1.65/×0.61 der Varianz,
    // d.h. max. ×1.28 der Vola pro Jahr) kam in KEINER Likelihood vor und
    // hätte mit korrigiertem γ > 0 legitimes Volatilitäts-Clustering in
    // Krisen abgeschnitten (z.B. Vola-Sprung 18% → 40–60% braucht
    // Δlog(σ²) ≈ 1.6–2.4). Explosionen sind ohne Dämpfung ausgeschlossen:
    // EGARCH ist mit |β| < 1 stationär, und die harten Bounds klemmen
    // jeden Einzelschritt auf den ökonomisch sinnvollen Bereich.
    const boundedLogVariance = Math.max(
      MIN_LOG_VARIANCE,
      Math.min(MAX_LOG_VARIANCE, newLogVariance)
    );

    // SCHRITT 6: Update state mit korrigierten Werten
    this.state = {
      logVariance: boundedLogVariance,
      lastReturn: currentReturn,
      lastVolatility: currentVolatility,
      period: this.state.period + 1,
    };
  }

  /**
   * Generiert Zufallswerte aus verschiedenen Verteilungen für Innovationen
   * 
   * Unterstützt:
   * - Normalverteilung (Standard)
   * - Student's t-Verteilung (für Heavy Tails)
   * 
   * Die Wahl der Verteilung wurde bereits im Konstruktor basierend auf Kurtosis bestimmt
   */
  private generateInnovation(): number {
    if (this.currentInnovationDistribution === 'student-t') {
      return this.generateTDistributionRandom();
    } else {
      return this.generateNormalRandom();
    }
  }

  /**
   * Generiert normalverteilten Zufallswert (Box-Muller)
   * Nutzt Math.log(1 - u1) statt Math.log(u1) zur Vermeidung von log(0) bei Randwerten
   */
  private generateNormalRandom(): number {
    const u1 = this.rng();
    const u2 = this.rng();
    return Math.sqrt(-2 * Math.log(1 - u1)) * Math.cos(2 * Math.PI * u2);
  }

  /**
   * Generiert varianz-standardisierten Student's t-verteilten Zufallswert
   *
   * Verwendet die im Konstruktor aus der EXCESS-Kurtosis geschätzten
   * Freiheitsgrade ν = 4 + 6/ExcessKurtosis (geklemmt auf [3, 30]).
   *
   * WICHTIG: Eine rohe t-Variate hat Varianz ν/(ν−2). Damit die Innovation
   * Var(ε)=1 erfüllt (Voraussetzung des EGARCH-Modells), wird mit
   * √((ν−2)/ν) standardisiert.
   */
  private generateTDistributionRandom(): number {
    // Ganzzahlige Freiheitsgrade aus dem Konstruktor (konsistent mit χ²-Erzeugung)
    const degreesOfFreedom = Math.max(3, Math.round(this.studentTDegreesOfFreedom));

    // t-Verteilung als Normal / sqrt(χ²/ν) — Summe UND Division mit derselben ganzen Zahl
    const normal = this.generateNormalRandom();
    const chiSquared = this.generateChiSquaredRandom(degreesOfFreedom);
    const tVariate = normal / Math.sqrt(chiSquared / degreesOfFreedom);

    // Standardisierung auf Var(ε)=1: t-Variate hat Varianz ν/(ν−2)
    return tVariate * Math.sqrt((degreesOfFreedom - 2) / degreesOfFreedom);
  }

  /**
   * Generiert Chi-Quadrat-verteilten Zufallswert mit ganzzahligen Freiheitsgraden
   *
   * Exakt als Summe von df quadrierten Standardnormalverteilten.
   * df wird auf eine ganze Zahl gerundet — der Aufrufer muss dieselbe
   * ganze Zahl für die Division verwenden (siehe generateTDistributionRandom).
   */
  private generateChiSquaredRandom(degreesOfFreedom: number): number {
    const intDf = Math.max(1, Math.round(degreesOfFreedom));
    let sum = 0;
    for (let i = 0; i < intDf; i++) {
      const normal = this.generateNormalRandom();
      sum += normal * normal;
    }
    return sum;
  }

  /**
   * Gamma-Funktion für E[|t_ν|] Berechnung
   *
   * Lanczos-Approximation (g=7, n=9): relative Genauigkeit ~1e-13 für x > 0.
   * Ersetzt die frühere Stirling-Approximation, die für kleine Argumente
   * (z.B. x=1.5) mehrere Prozent Fehler hatte.
   */
  private gammaFunction(x: number): number {
    const g = 7;
    const coefficients = [
      0.99999999999980993, 676.5203681218851, -1259.1392167224028,
      771.32342877765313, -176.61502916214059, 12.507343278686905,
      -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
    ];

    if (x < 0.5) {
      // Reflexionsformel: Γ(x) = π / (sin(πx)·Γ(1−x))
      return Math.PI / (Math.sin(Math.PI * x) * this.gammaFunction(1 - x));
    }

    const z = x - 1;
    let a = coefficients[0];
    const t = z + g + 0.5;
    for (let i = 1; i < coefficients.length; i++) {
      a += coefficients[i] / (z + i);
    }

    return Math.sqrt(2 * Math.PI) * Math.pow(t, z + 0.5) * Math.exp(-t) * a;
  }

  /**
   * Leitet deterministischen Kalibrierungs-Seed aus der Renditereihe ab
   *
   * Einfacher 32-bit Hash über die (auf 4 Nachkommastellen gerundeten) Renditen:
   * identische Eingangsdaten ⇒ identischer Seed ⇒ identische Kalibrierung.
   */
  private deriveCalibrationSeed(returns: number[]): number {
    let hash = 0;
    for (const r of returns) {
      const value = Math.round(r * 10000);
      hash = ((hash << 5) - hash + value) | 0; // 32-bit Integer
    }
    return Math.abs(hash) || 1; // Seed 0 vermeiden (LCG-Fixpunkt-Risiko)
  }

  /**
   * Erstellt seeded RNG für reproduzierbare Tests
   */
  private createSeededRNG(seed: number): () => number {
    let currentSeed = seed;
    return () => {
      currentSeed = (currentSeed * 1664525 + 1013904223) % Math.pow(2, 32);
      return currentSeed / Math.pow(2, 32);
    };
  }

  // ===== DIAGNOSTIC METHODS =====

  /**
   * Führt umfassende Modell-Diagnostik durch
   * 
   * Implementiert Standard-GARCH-Diagnostik-Tests:
   * - Ljung-Box Test für Residuen-Autokorrelation
   * - ARCH-LM Test für verbleibende ARCH-Effekte
   * - Sign Bias Test für Asymmetrie-Validierung
   * - Jarque-Bera Test für Normalität der standardisierten Residuen
   * 
   * @param returns Historische Renditen für Diagnostik
   * @returns Vollständige Diagnostik-Ergebnisse
   */
  public runDiagnostics(returns: number[]): DiagnosticResults {
    // Running comprehensive EGARCH diagnostics...
    
    // SCHRITT 1: Berechne Model Fit und standardisierte Residuen
    const modelFit = this.calculateModelFit(returns);
    
    // SCHRITT 2: Ljung-Box Test für Autokorrelation
    const ljungBox = this.ljungBoxTest(modelFit.standardizedResiduals);
    
    // SCHRITT 3: ARCH-LM Test für verbleibende ARCH-Effekte
    const archLM = this.archLMTest(modelFit.standardizedResiduals);
    
    // SCHRITT 4: Sign Bias Test für Asymmetrie
    const signBias = this.signBiasTest(returns, modelFit.standardizedResiduals);
    
    // SCHRITT 5: Jarque-Bera Test für Normalität
    const jarqueBera = this.jarqueBeraTest(modelFit.standardizedResiduals);
    
    // SCHRITT 6: Gesamtbewertung
    const overallAssessment = this.assessOverallModelAdequacy({
      ljungBox, archLM, signBias, jarqueBera
    });
    
    const results: DiagnosticResults = {
      ljungBox,
      archLM,
      signBias,
      jarqueBera,
      overallAssessment
    };
    
    // EGARCH diagnostic results computed
    return results;
  }

  /**
   * Berechnet Model Fit mit standardisierten Residuen
   */
  private calculateModelFit(returns: number[]): ModelFit {
    const n = returns.length;
    const mean = returns.reduce((sum, r) => sum + r, 0) / n;
    const { omega, alpha, theta, gamma, beta } = this.calibrationData.garchParams;
    
    const standardizedResiduals: number[] = [];
    const fittedVolatilities: number[] = [];
    let logLikelihood = 0;
    
    // Initialisiere mit langfristigem Durchschnitt aus historischen Daten
    const stats = calculateAllStatistics(returns);
    let logVariance = Math.log(Math.pow(stats.stdDev / 100, 2));

    for (let t = 1; t < n; t++) {
      // Berechne aktuelle Volatilität
      const currentVolatility = Math.sqrt(Math.exp(logVariance));
      fittedVolatilities.push(currentVolatility * 100); // In Prozent

      // Berechne standardisierte Residuen
      const currentReturn = returns[t] / 100;
      const meanDecimal = mean / 100;
      const epsilon = (currentReturn - meanDecimal) / currentVolatility;
      standardizedResiduals.push(epsilon);

      // Log-Likelihood Beitrag
      logLikelihood += -0.5 * (Math.log(2 * Math.PI) + logVariance + epsilon * epsilon);

      // LAG-FIX: Update von σ²ₜ₊₁ mit AKTUELLEM Schock εₜ
      // log(σₜ₊₁²) = ω + β·log(σₜ²) + α·g(εₜ) — konsistent mit updateGARCHState
      const expectedAbsEpsilon = Math.sqrt(2 / Math.PI);
      const gFunction = theta * epsilon + gamma * (Math.abs(epsilon) - expectedAbsEpsilon);
      const newLogVariance = omega + beta * logVariance + alpha * gFunction;
      // Einheitliche Bounds (Fix #16), identisch zu Likelihood und Simulation
      logVariance = Math.max(MIN_LOG_VARIANCE, Math.min(MAX_LOG_VARIANCE, newLogVariance));
    }
    
    // Informationskriterien
    const numParams = 5; // ω, α, θ, γ, β
    const aic = -2 * logLikelihood + 2 * numParams;
    const bic = -2 * logLikelihood + numParams * Math.log(n);
    
    return {
      standardizedResiduals,
      fittedVolatilities,
      logLikelihood,
      aic,
      bic
    };
  }

  /**
   * Ljung-Box Test für Autokorrelation in standardisierten Residuen
   * 
   * H₀: Keine Autokorrelation in den Residuen
   * H₁: Autokorrelation vorhanden
   * 
   * Test-Statistik: Q = n(n+2) * Σ(ρₖ²/(n-k)) ~ χ²(m)
   */
  private ljungBoxTest(residuals: number[], lags: number = 10) {
    const n = residuals.length;
    if (n < lags + 1) {
      return { statistic: 0, pValue: 1, lags, passed: false };
    }
    
    // Berechne Autokorrelationen
    const mean = residuals.reduce((sum, r) => sum + r, 0) / n;
    const variance = residuals.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / n;
    
    let qStatistic = 0;
    for (let k = 1; k <= lags; k++) {
      let autocovariance = 0;
      for (let t = k; t < n; t++) {
        autocovariance += (residuals[t] - mean) * (residuals[t - k] - mean);
      }
      autocovariance /= n;
      
      const autocorrelation = autocovariance / variance;
      qStatistic += (autocorrelation * autocorrelation) / (n - k);
    }
    
    qStatistic *= n * (n + 2);
    
    // P-Value Approximation (Chi-Square mit lags Freiheitsgraden)
    const pValue = 1 - this.chiSquareCDF(qStatistic, lags);
    const passed = pValue > 0.05; // 5% Signifikanz-Level
    
    return { statistic: qStatistic, pValue, lags, passed };
  }

  /**
   * ARCH-LM Test für verbleibende ARCH-Effekte
   * 
   * H₀: Keine ARCH-Effekte in standardisierten Residuen
   * H₁: ARCH-Effekte vorhanden
   * 
   * Regression: ε²ₜ = α₀ + α₁ε²ₜ₋₁ + ... + αₘε²ₜ₋ₘ + uₜ
   * Test-Statistik: LM = n * R² ~ χ²(m)
   */
  private archLMTest(residuals: number[], lags: number = 5) {
    const n = residuals.length;
    if (n < lags + 10) {
      return { statistic: 0, pValue: 1, lags, passed: false };
    }
    
    // Berechne quadrierte Residuen
    const squaredResiduals = residuals.map(r => r * r);
    
    // Regression von ε²ₜ auf ε²ₜ₋₁, ..., ε²ₜ₋ₘ
    const y: number[] = [];
    const X: number[][] = [];
    
    for (let t = lags; t < n; t++) {
      y.push(squaredResiduals[t]);
      const row = [1]; // Konstante
      for (let j = 1; j <= lags; j++) {
        row.push(squaredResiduals[t - j]);
      }
      X.push(row);
    }
    
    // Vereinfachte OLS Regression für R²
    const yMean = y.reduce((sum, val) => sum + val, 0) / y.length;
    
    // R² Berechnung (vereinfacht)
    let ssTotal = 0;
    let ssResidual = 0;
    
    for (let i = 0; i < y.length; i++) {
      ssTotal += Math.pow(y[i] - yMean, 2);
      
      // Einfache Vorhersage basierend auf Durchschnitt der Lags
      let prediction = 0;
      for (let j = 1; j <= lags; j++) {
        if (X[i][j] !== undefined) {
          prediction += X[i][j] / lags;
        }
      }
      ssResidual += Math.pow(y[i] - prediction, 2);
    }
    
    const rSquared = Math.max(0, 1 - ssResidual / ssTotal);
    const lmStatistic = (n - lags) * rSquared;
    
    // P-Value Approximation
    const pValue = 1 - this.chiSquareCDF(lmStatistic, lags);
    const passed = pValue > 0.05;
    
    return { statistic: lmStatistic, pValue, lags, passed };
  }

  /**
   * Sign Bias Test für Asymmetrie-Validierung
   * 
   * Testet ob positive und negative Schocks unterschiedlich auf Volatilität wirken
   * Regression: ε²ₜ = α₀ + α₁S⁻ₜ₋₁ + α₂S⁻ₜ₋₁εₜ₋₁ + α₃S⁺ₜ₋₁εₜ₋₁ + uₜ
   */
  private signBiasTest(returns: number[], residuals: number[]) {
    const n = Math.min(returns.length, residuals.length);
    if (n < 30) {
      return { negativeSignBias: 0, positiveSignBias: 0, jointTest: 0, pValue: 1, passed: false };
    }
    
    const squaredResiduals = residuals.map(r => r * r);
    
    // Erstelle Regressoren
    const y: number[] = [];
    const negativeSign: number[] = [];
    const negativeSignResidual: number[] = [];
    const positiveSignResidual: number[] = [];
    
    for (let t = 1; t < n - 1; t++) {
      y.push(squaredResiduals[t + 1]);
      
      // S⁻ₜ = 1 wenn εₜ < 0, sonst 0
      const isNegative = residuals[t] < 0 ? 1 : 0;
      negativeSign.push(isNegative);
      
      // S⁻ₜ * εₜ
      negativeSignResidual.push(isNegative * residuals[t]);
      
      // S⁺ₜ * εₜ (positive Schocks)
      const isPositive = residuals[t] >= 0 ? 1 : 0;
      positiveSignResidual.push(isPositive * residuals[t]);
    }
    
    // Vereinfachte Korrelationsanalyse für Sign Bias
    const negativeSignBias = this.calculateCorrelation(y, negativeSignResidual);
    const positiveSignBias = this.calculateCorrelation(y, positiveSignResidual);
    
    // Joint Test (vereinfacht)
    const jointTest = Math.abs(negativeSignBias) + Math.abs(positiveSignBias);
    
    // Approximiere P-Value
    const pValue = Math.max(0.01, Math.min(0.99, 1 - jointTest));
    const passed = Math.abs(negativeSignBias) < 0.3 && Math.abs(positiveSignBias) < 0.3;
    
    return { negativeSignBias, positiveSignBias, jointTest, pValue, passed };
  }

  /**
   * Jarque-Bera Test für Normalität der standardisierten Residuen
   * 
   * H₀: Residuen sind normalverteilt
   * H₁: Residuen sind nicht normalverteilt
   * 
   * JB = n/6 * (S² + (K-3)²/4) ~ χ²(2)
   */
  private jarqueBeraTest(residuals: number[]) {
    const n = residuals.length;
    if (n < 10) {
      return { statistic: 0, pValue: 1, passed: false };
    }
    
    // Berechne Momente
    const mean = residuals.reduce((sum, r) => sum + r, 0) / n;
    const variance = residuals.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / n;
    const stdDev = Math.sqrt(variance);
    
    let m3 = 0; // Drittes Moment
    let m4 = 0; // Viertes Moment
    
    for (const r of residuals) {
      const deviation = r - mean;
      m3 += Math.pow(deviation, 3);
      m4 += Math.pow(deviation, 4);
    }
    
    m3 /= n;
    m4 /= n;
    
    // Schiefe und Kurtosis
    const skewness = m3 / Math.pow(stdDev, 3);
    const kurtosis = m4 / Math.pow(stdDev, 4);
    
    // Jarque-Bera Statistik
    const jbStatistic = (n / 6) * (skewness * skewness + Math.pow(kurtosis - 3, 2) / 4);
    
    // P-Value (Chi-Square mit 2 Freiheitsgraden)
    const pValue = 1 - this.chiSquareCDF(jbStatistic, 2);
    const passed = pValue > 0.05;
    
    return { statistic: jbStatistic, pValue, passed };
  }

  /**
   * Gesamtbewertung der Modell-Adäquatheit
   */
  private assessOverallModelAdequacy(tests: any) {
    const issues: string[] = [];
    const recommendations: string[] = [];
    
    // Ljung-Box Test
    if (!tests.ljungBox.passed) {
      issues.push('Autokorrelation in standardisierten Residuen detected');
      recommendations.push('Consider higher order EGARCH model or different innovation distribution');
    }
    
    // ARCH-LM Test
    if (!tests.archLM.passed) {
      issues.push('Remaining ARCH effects in standardized residuals');
      recommendations.push('Increase ARCH order or check parameter estimation');
    }
    
    // Sign Bias Test
    if (!tests.signBias.passed) {
      issues.push('Asymmetric effects not properly captured');
      recommendations.push('Verify leverage parameter θ and consider GJR-GARCH alternative');
    }
    
    // Jarque-Bera Test
    if (!tests.jarqueBera.passed) {
      issues.push('Standardized residuals not normally distributed');
      recommendations.push('Consider Student-t or skewed-t innovation distribution');
    }
    
    const passed = issues.length === 0;
    
    if (passed) {
      recommendations.push('Model appears adequate for volatility forecasting');
    }
    
    return { passed, issues, recommendations };
  }

  /**
   * Chi-Square CDF Approximation
   */
  private chiSquareCDF(x: number, df: number): number {
    if (x <= 0) {return 0;}
    if (df <= 0) {return 0;}
    
    // Vereinfachte Approximation für Chi-Square CDF
    // Für df = 1: CDF ≈ 2 * Φ(√x) - 1
    // Für df > 1: Verwende Gamma-Funktion Approximation
    
    if (df === 1) {
      return 2 * this.normalCDF(Math.sqrt(x)) - 1;
    } else if (df === 2) {
      return 1 - Math.exp(-x / 2);
    } else {
      // Allgemeine Approximation (Wilson-Hilferty)
      const h = 2 / (9 * df);
      const z = (Math.pow(x / df, 1/3) - 1 + h) / Math.sqrt(h);
      return this.normalCDF(z);
    }
  }

  /**
   * Standard Normal CDF Approximation
   */
  private normalCDF(x: number): number {
    // Abramowitz und Stegun Approximation
    const a1 =  0.254829592;
    const a2 = -0.284496736;
    const a3 =  1.421413741;
    const a4 = -1.453152027;
    const a5 =  1.061405429;
    const p  =  0.3275911;
    
    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);
    
    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
    
    return 0.5 * (1.0 + sign * y);
  }

  /**
   * Berechnet Korrelation zwischen zwei Arrays
   */
  private calculateCorrelation(x: number[], y: number[]): number {
    const n = Math.min(x.length, y.length);
    if (n === 0) {return 0;}
    
    const xMean = x.reduce((sum, val) => sum + val, 0) / n;
    const yMean = y.reduce((sum, val) => sum + val, 0) / n;
    
    let numerator = 0;
    let xVariance = 0;
    let yVariance = 0;
    
    for (let i = 0; i < n; i++) {
      const xDev = x[i] - xMean;
      const yDev = y[i] - yMean;
      
      numerator += xDev * yDev;
      xVariance += xDev * xDev;
      yVariance += yDev * yDev;
    }
    
    const denominator = Math.sqrt(xVariance * yVariance);
    return denominator === 0 ? 0 : numerator / denominator;
  }
}

/**
 * Integration-Layer für nahtlose Verwendung mit bestehenden RateCalculationService
 *
 * Diese Klasse stellt die Brücke zwischen GARCH-Modellierung und den
 * bestehenden Rate-Berechnungsoptionen dar.
 */
export class GARCHRateCalculationIntegration {
  /**
   * Erweitert bestehende Investitionstopf-Rate-Optionen um GARCH-Varianten
   *
   * Kann direkt in RateCalculationService.calculateInvestitionstopfRate() integriert werden
   *
   * @param option - Erweiterte Option: 'historische-garch' oder 'statistisch-garch'
   * @param years - Anzahl Jahre für die Simulation
   * @param indexType - Welcher Index ('world' oder 'acwi')
   * @param seed - Optional: Seed für Monte Carlo Reproduzierbarkeit
   * @returns Array von jährlichen Renditen in Prozent
   */
  static generateInvestitionstopfReturns(
    option: 'historische-garch' | 'statistisch-garch',
    years: number,
    indexType: 'world' | 'acwi' = 'world',
    seed?: number
  ): number[] {
    // Map German option names to English GARCH mode names
    const garchMode = option === 'historische-garch' ? 'historical-garch' : 'statistical-garch';
    
    // Use cached generator to avoid expensive re-calibration
    const garch = DataIntegratedGARCHGenerator.getCachedGenerator(indexType, garchMode);
    
    // Reset generator state with new seed for this iteration
    garch.resetForNewIteration(seed);
    
    const returns = garch.generateAnnualReturns(years);

    return returns;
  }

  /**
   * Erweitert bestehende RateCalculationService für einzelne Rendite mit GARCH-Eigenschaften
   *
   * Für Kompatibilität mit bestehenden single-rate APIs
   */
  static generateSingleGARCHRate(
    option: 'historische-garch' | 'statistisch-garch',
    indexType: 'world' | 'acwi' = 'world',
    seed?: number
  ): number {
    // Map German option names to English GARCH mode names
    const garchMode = option === 'historische-garch' ? 'historical-garch' : 'statistical-garch';
    
    // Use cached generator to avoid expensive re-calibration
    const garch = DataIntegratedGARCHGenerator.getCachedGenerator(indexType, garchMode);
    
    // Reset generator state with new seed for this iteration
    garch.resetForNewIteration(seed);
    
    return garch.generateAnnualReturn();
  }

  /**
   * Analysiert bestehende Index-Daten und gibt GARCH-Kalibrierung zurück
   *
   * ENHANCED v2.4.2: Inkludiert Schätzungsmethoden-Informationen für UI
   */
  static analyzeIndexDataForGARCH(indexType: 'world' | 'acwi' = 'world') {
    const garch = new DataIntegratedGARCHGenerator(indexType, 'historical-garch');
    const calibration = garch.getCalibrationData();
    const estimationInfo = garch.getEstimationInfo();

    return {
      indexType,
      dataPoints: calibration.historicalReturns.length,
      historicalMean: calibration.statistics.mean.toFixed(2),
      historicalVolatility: calibration.statistics.stdDev.toFixed(2),
      skewness: calibration.statistics.skewness.toFixed(3),
      kurtosis: calibration.statistics.kurtosis.toFixed(3),
      garchParameters: {
        volatilityPersistence: calibration.garchParams.beta.toFixed(3),
        archEffect: calibration.garchParams.alpha.toFixed(3),
        // LABEL-FIX: θ ist der SIGNIERTE Term in g(ε) und damit der Leverage-Effekt;
        // γ ist der MAGNITUDEN-Term und damit das Volatilitäts-Clustering.
        // Vorher waren die Begriffe vertauscht (γ wurde als "leverageEffect" gemeldet).
        leverageEffect: calibration.garchParams.theta.toFixed(3),
        clusteringEffect: calibration.garchParams.gamma.toFixed(3),
      },
      estimationQuality: {
        method: estimationInfo.method,
        quality: estimationInfo.quality,
        description: estimationInfo.description,
        isFallback: estimationInfo.isFallback,
        logLikelihood: estimationInfo.logLikelihood?.toFixed(2),
        iterations: estimationInfo.iterations
      }
    };
  }

  /**
   * Vergleicht GARCH-Renditen mit bestehenden Methoden
   *
   * Für Validierung und User-Education
   */
  static compareGARCHWithExistingMethods(years: number = 30, iterations: number = 1000) {
    const results = {
      'historische-rendite': [] as number[],
      'historische-garch': [] as number[],
      'statistisch-garch': [] as number[],
    };

    // Bestehende historische Methode (vereinfacht simuliert)
    const indexData = getIndexStatistics('world');
    for (let i = 0; i < iterations; i++) {
      const finalBalance = this.simulatePortfolioGrowth(
        Array.from(
          { length: years },
          () => indexData.rates[Math.floor(Math.random() * indexData.rates.length)]
        )
      );
      results['historische-rendite'].push(finalBalance);
    }

    // Neue GARCH-Methoden
    for (const method of ['historische-garch', 'statistisch-garch'] as const) {
      for (let i = 0; i < iterations; i++) {
        const garchReturns = this.generateInvestitionstopfReturns(method, years, 'world', i);
        const finalBalance = this.simulatePortfolioGrowth(garchReturns);
        results[method].push(finalBalance);
      }
    }

    // Analysiere Ergebnisse
    const comparison: any = {};
    for (const [method, balances] of Object.entries(results)) {
      const stats = calculateAllStatistics(balances);
      comparison[method] = {
        mean: stats.mean,
        stdDev: stats.stdDev,
        percentile10: stats.p10,
        percentile90: stats.p90,
      };
    }

    return comparison;
  }

  /**
   * Einfache Portfolio-Simulation für Vergleichszwecke
   */
  private static simulatePortfolioGrowth(
    annualReturns: number[],
    startValue: number = 1000000
  ): number {
    let value = startValue;
    for (const returnPercent of annualReturns) {
      value *= 1 + returnPercent / 100;
      value -= 50000; // 50k jährliche Entnahme
      if (value <= 0) {break;}
    }
    return Math.max(0, value);
  }
}

/**
 * Utility-Funktionen für bestehende Integration
 */
export class GARCHUtils {
  /**
   * Prüft ob eine Option eine GARCH-Variante ist
   */
  static isGARCHOption(option: string): boolean {
    return option.includes('garch');
  }

  /**
   * Konvertiert bestehende Optionen zu GARCH-Äquivalenten
   */
  static convertToGARCHOption(existingOption: string): string | null {
    switch (existingOption) {
      case 'historische-rendite':
        return 'historische-garch';
      case 'statistisch-rendite':
        return 'statistisch-garch';
      default:
        return null;
    }
  }

  /**
   * Gibt User-freundliche Beschreibung für GARCH-Optionen zurück
   */
  static getGARCHOptionDescription(option: string): string {
    switch (option) {
      case 'historische-garch':
        return 'Historische Renditen mit realistischer Volatilitäts-Struktur (Krisen-Cluster, Leverage-Effekte)';
      case 'statistisch-garch':
        return 'Statistische Verteilung mit zeitvariabler Volatilität (Mean Reversion, Fat Tails)';
      default:
        return 'Unbekannte GARCH-Option';
    }
  }
}

// Export für Backward-Compatibility mit bestehenden Tests
export { DataIntegratedGARCHGenerator as EGARCHGenerator };
export { GARCHRateCalculationIntegration as GARCHIntegration };

// Legacy-Support für bestehende GARCH_PRESETS (mapped zu neuen Daten-getriebenen Ansatz)
export const GARCH_PRESETS = {
  conservative: { indexType: 'world' as const, mode: 'statistical-garch' as const },
  realistic: { indexType: 'world' as const, mode: 'historical-garch' as const },
  stress: { indexType: 'acwi' as const, mode: 'historical-garch' as const },
};
